import { useId, useRef } from 'react';

/** Provides automatic arrow-key activation and matching tab/panel identifiers. */
export default function useAccessibleTabs({ tabs, activeTab, onSelect, label }) {
  const generatedId = useId().replace(/:/g, '');
  const refs = useRef(new Map());
  const prefix = `tabs-${generatedId}`;

  const selectFromKey = (event, tab) => {
    const current = tabs.indexOf(tab);
    let next = current;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
      next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    const nextTab = tabs[next];
    onSelect(nextTab);
    refs.current.get(nextTab)?.focus();
  };

  const key = (tab) =>
    String(tab)
      .replace(/[^a-z0-9_-]+/gi, '-')
      .toLowerCase();

  return {
    tabListProps: { role: 'tablist', 'aria-label': label },
    getTabProps: (tab) => ({
      id: `${prefix}-tab-${key(tab)}`,
      role: 'tab',
      'aria-selected': activeTab === tab,
      'aria-controls': `${prefix}-panel-${key(tab)}`,
      tabIndex: activeTab === tab ? 0 : -1,
      ref: (node) => {
        if (node) refs.current.set(tab, node);
        else refs.current.delete(tab);
      },
      onKeyDown: (event) => selectFromKey(event, tab),
    }),
    panelProps: {
      id: `${prefix}-panel-${key(activeTab)}`,
      role: 'tabpanel',
      'aria-labelledby': `${prefix}-tab-${key(activeTab)}`,
      tabIndex: 0,
    },
  };
}
